import React, { useState, useEffect, useCallback } from "react";
import { api, useBusiness, useAuth } from "@/App";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const CATEGORIES = {
  income: { name: "Income / Revenue", color: "#3b82f6", bg: "bg-blue-50 border-blue-200 text-blue-700" },
  profit: { name: "Profit", color: "#10b981", bg: "bg-[#1e3a5f]/5 border-[#1e3a5f]/20 text-[#1e3a5f]" },
  owner_pay: { name: "Owner's Pay", color: "#8b5cf6", bg: "bg-purple-50 border-purple-200 text-purple-700" },
  tax: { name: "Tax", color: "#f59e0b", bg: "bg-amber-50 border-amber-200 text-amber-700" },
  opex: { name: "Operating Expenses", color: "#ef4444", bg: "bg-red-50 border-red-200 text-red-700" },
  uncategorized: { name: "Uncategorized", color: "#9ca3af", bg: "bg-stone-100 border-stone-300 text-stone-600" },
};

const RECURRING_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount || 0);

const Transactions = () => {
  const { selectedBusiness } = useBusiness();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const initialAccount = searchParams.get("account") || "all";
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");

  // Filters
  const [filterAccount, setFilterAccount] = useState(initialAccount);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Add transaction dialog
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingTxn, setEditingTxn] = useState(null);
  const [txnForm, setTxnForm] = useState({
    bank_account_id: "",
    date: new Date().toISOString().split("T")[0],
    description: "",
    amount: "",
    type: "expense",
    category: "uncategorized",
    vendor_name: "",
    is_recurring: false,
    recurring_frequency: "",
    notes: "",
  });

  // Receipt upload
  const [uploadingReceipt, setUploadingReceipt] = useState(null);

  // Bulk categorize
  const [selectedTxns, setSelectedTxns] = useState(new Set());
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkCategory, setBulkCategory] = useState("opex");

  const fetchData = useCallback(async () => {
    if (!selectedBusiness) return;
    setLoading(true);
    try {
      const [txnRes, summaryRes, accountsRes] = await Promise.all([
        api.get(`/transactions/${selectedBusiness.id}`),
        api.get(`/transactions/${selectedBusiness.id}/summary`),
        api.get(`/manual-bank-accounts/${selectedBusiness.id}`),
      ]);
      setTransactions(txnRes.data);
      setSummary(summaryRes.data);
      setBankAccounts(accountsRes.data);
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  }, [selectedBusiness]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setTxnForm({
      bank_account_id: bankAccounts[0]?.id || "",
      date: new Date().toISOString().split("T")[0],
      description: "",
      amount: "",
      type: "expense",
      category: "uncategorized",
      vendor_name: "",
      is_recurring: false,
      recurring_frequency: "",
      notes: "",
    });
    setEditingTxn(null);
  };

  const openAddDialog = () => {
    resetForm();
    if (bankAccounts.length > 0) {
      setTxnForm((prev) => ({ ...prev, bank_account_id: bankAccounts[0].id }));
    }
    setShowAddDialog(true);
  };

  const openEditDialog = (txn) => {
    setEditingTxn(txn);
    setTxnForm({
      bank_account_id: txn.bank_account_id,
      date: txn.date,
      description: txn.description,
      amount: txn.amount.toString(),
      type: txn.type,
      category: txn.category,
      vendor_name: txn.vendor_name || "",
      is_recurring: txn.is_recurring || false,
      recurring_frequency: txn.recurring_frequency || "",
      notes: txn.notes || "",
    });
    setShowAddDialog(true);
  };

  const saveTransaction = async () => {
    if (!txnForm.description || !txnForm.amount || !txnForm.bank_account_id) {
      toast.error("Please fill in description, amount, and select a bank account");
      return;
    }
    try {
      if (editingTxn) {
        await api.put(`/transactions/${editingTxn.id}`, {
          date: txnForm.date,
          description: txnForm.description,
          amount: parseFloat(txnForm.amount),
          type: txnForm.type,
          category: txnForm.category,
          vendor_name: txnForm.vendor_name || null,
          is_recurring: txnForm.is_recurring,
          recurring_frequency: txnForm.is_recurring ? txnForm.recurring_frequency : null,
          notes: txnForm.notes || null,
        });
        toast.success("Transaction updated");
      } else {
        await api.post("/transactions", {
          business_id: selectedBusiness.id,
          bank_account_id: txnForm.bank_account_id,
          date: txnForm.date,
          description: txnForm.description,
          amount: parseFloat(txnForm.amount),
          type: txnForm.type,
          category: txnForm.category,
          vendor_name: txnForm.vendor_name || null,
          is_recurring: txnForm.is_recurring,
          recurring_frequency: txnForm.is_recurring ? txnForm.recurring_frequency : null,
          notes: txnForm.notes || null,
        });
        toast.success("Transaction added");
      }
      setShowAddDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save transaction");
    }
  };

  const deleteTransaction = async (txnId) => {
    try {
      await api.delete(`/transactions/${txnId}`);
      toast.success("Transaction deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete transaction");
    }
  };

  const categorizeTransaction = async (txnId, category) => {
    try {
      await api.put(`/transactions/${txnId}`, { category });
      toast.success("Category updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update category");
    }
  };

  const handleBulkCategorize = async () => {
    if (selectedTxns.size === 0) return;
    try {
      await api.put("/transactions/bulk-categorize", {
        transaction_ids: Array.from(selectedTxns),
        category: bulkCategory,
      });
      toast.success(`${selectedTxns.size} transactions categorized`);
      setSelectedTxns(new Set());
      setShowBulkDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to bulk categorize");
    }
  };

  const handleReceiptUpload = async (txnId, file) => {
    setUploadingReceipt(txnId);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/transactions/${txnId}/receipt`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Receipt uploaded");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload receipt");
    } finally {
      setUploadingReceipt(null);
    }
  };

  const toggleSelectTxn = (txnId) => {
    setSelectedTxns((prev) => {
      const next = new Set(prev);
      if (next.has(txnId)) next.delete(txnId);
      else next.add(txnId);
      return next;
    });
  };

  // Filtered transactions
  const filtered = transactions.filter((t) => {
    if (filterAccount !== "all" && t.bank_account_id !== filterAccount) return false;
    if (filterCategory !== "all" && t.category !== filterCategory) return false;
    if (filterType !== "all" && t.type !== filterType) return false;
    if (searchQuery && !t.description.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !(t.vendor_name || "").toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const uncategorized = transactions.filter((t) => t.category === "uncategorized");
  const getAccountName = (id) => bankAccounts.find((a) => a.id === id)?.account_name || "Unknown";

  if (!selectedBusiness) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-muted-foreground">Please select a business to view transactions.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-pulse text-muted-foreground">Loading transactions...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="transactions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">Transactions</h1>
          <p className="text-stone-600">Categorize expenses to your Smart Account allocations</p>
        </div>
        <div className="flex gap-3">
          {selectedTxns.size > 0 && (
            <Button variant="outline" onClick={() => setShowBulkDialog(true)} data-testid="bulk-categorize-btn">
              Categorize {selectedTxns.size} selected
            </Button>
          )}
          <Button onClick={openAddDialog} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="add-transaction-btn">
            + Add Transaction
          </Button>
        </div>
      </div>

      {/* No bank accounts warning */}
      {bankAccounts.length === 0 && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-6">
            <p className="text-amber-800 font-medium">No bank accounts found. Please add a bank account first to start tracking transactions.</p>
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-5 pb-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(summary.total_income)}</p>
              <p className="text-xs text-stone-500 mt-1">Total Income</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4 text-center">
              <p className="text-2xl font-bold text-red-600">{formatCurrency(summary.total_expenses)}</p>
              <p className="text-xs text-stone-500 mt-1">Total Expenses</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4 text-center">
              <p className={`text-2xl font-bold ${summary.net >= 0 ? "text-[#1e3a5f]" : "text-red-600"}`}>
                {formatCurrency(summary.net)}
              </p>
              <p className="text-xs text-stone-500 mt-1">Net</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4 text-center">
              <p className="text-2xl font-bold text-stone-700">{summary.total_transactions}</p>
              <p className="text-xs text-stone-500 mt-1">Transactions</p>
            </CardContent>
          </Card>
          <Card className={summary.uncategorized_count > 0 ? "border-amber-300 bg-amber-50" : ""}>
            <CardContent className="pt-5 pb-4 text-center">
              <p className={`text-2xl font-bold ${summary.uncategorized_count > 0 ? "text-amber-600" : "text-stone-400"}`}>
                {summary.uncategorized_count}
              </p>
              <p className="text-xs text-stone-500 mt-1">Needs Categorization</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Category Allocation Breakdown */}
      {summary?.category_breakdown && Object.keys(summary.category_breakdown).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Allocation Breakdown</CardTitle>
            <CardDescription>How your transactions map to Smart Account categories</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(CATEGORIES).map(([key, cat]) => {
                const data = summary.category_breakdown[key];
                return (
                  <div key={key} className={`p-3 rounded-lg border ${cat.bg}`} data-testid={`category-${key}`}>
                    <p className="text-xs font-medium opacity-80">{cat.name}</p>
                    <p className="text-lg font-bold mt-1">{formatCurrency(data?.total || 0)}</p>
                    <p className="text-xs opacity-60">{data?.count || 0} items</p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs: All / Uncategorized */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <TabsList>
            <TabsTrigger value="all" data-testid="tab-all-transactions">
              All ({transactions.length})
            </TabsTrigger>
            <TabsTrigger value="uncategorized" data-testid="tab-uncategorized">
              Needs Review ({uncategorized.length})
            </TabsTrigger>
          </TabsList>

          {/* Filters */}
          <div className="flex flex-wrap gap-2">
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-40"
              data-testid="search-transactions"
            />
            <Select value={filterAccount} onValueChange={setFilterAccount}>
              <SelectTrigger className="w-40" data-testid="filter-account">
                <SelectValue placeholder="All Accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {bankAccounts.map((a) => (
                  <SelectItem key={a.id} value={a.id}>{a.account_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-40" data-testid="filter-category">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(CATEGORIES).map(([key, cat]) => (
                  <SelectItem key={key} value={key}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-32" data-testid="filter-type">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
                <SelectItem value="income">Income</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <TabsContent value="all" className="mt-4">
          <TransactionList
            transactions={filtered}
            selectedTxns={selectedTxns}
            toggleSelectTxn={toggleSelectTxn}
            getAccountName={getAccountName}
            onEdit={openEditDialog}
            onDelete={deleteTransaction}
            onCategorize={categorizeTransaction}
            onReceiptUpload={handleReceiptUpload}
            uploadingReceipt={uploadingReceipt}
            token={token}
          />
        </TabsContent>

        <TabsContent value="uncategorized" className="mt-4">
          {uncategorized.length === 0 ? (
            <Card>
              <CardContent className="pt-8 pb-8 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#1e3a5f]/10 flex items-center justify-center">
                  <svg className="w-7 h-7 text-[#1e3a5f]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-stone-700 font-medium">All transactions are categorized</p>
              </CardContent>
            </Card>
          ) : (
            <TransactionList
              transactions={uncategorized}
              selectedTxns={selectedTxns}
              toggleSelectTxn={toggleSelectTxn}
              getAccountName={getAccountName}
              onEdit={openEditDialog}
              onDelete={deleteTransaction}
              onCategorize={categorizeTransaction}
              onReceiptUpload={handleReceiptUpload}
              uploadingReceipt={uploadingReceipt}
              token={token}
            />
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Transaction Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTxn ? "Edit Transaction" : "Add Transaction"}</DialogTitle>
            <DialogDescription>
              {editingTxn ? "Update transaction details" : "Record a new transaction and assign it to an allocation category"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Bank Account *</Label>
              <Select
                value={txnForm.bank_account_id}
                onValueChange={(v) => setTxnForm({ ...txnForm, bank_account_id: v })}
              >
                <SelectTrigger data-testid="txn-bank-account">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {bankAccounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.account_name} ({a.bank_name})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={txnForm.date}
                  onChange={(e) => setTxnForm({ ...txnForm, date: e.target.value })}
                  data-testid="txn-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={txnForm.type} onValueChange={(v) => setTxnForm({ ...txnForm, type: v })}>
                  <SelectTrigger data-testid="txn-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="expense">Expense</SelectItem>
                    <SelectItem value="income">Income</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                placeholder="e.g., Office supplies from Staples"
                value={txnForm.description}
                onChange={(e) => setTxnForm({ ...txnForm, description: e.target.value })}
                data-testid="txn-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={txnForm.amount}
                  onChange={(e) => setTxnForm({ ...txnForm, amount: e.target.value })}
                  data-testid="txn-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Input
                  placeholder="e.g., Staples"
                  value={txnForm.vendor_name}
                  onChange={(e) => setTxnForm({ ...txnForm, vendor_name: e.target.value })}
                  data-testid="txn-vendor"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Allocation Category</Label>
              <Select value={txnForm.category} onValueChange={(v) => setTxnForm({ ...txnForm, category: v })}>
                <SelectTrigger data-testid="txn-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORIES).map(([key, cat]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }}></span>
                        {cat.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-stone-50">
              <Label className="text-sm">Recurring transaction?</Label>
              <Switch
                checked={txnForm.is_recurring}
                onCheckedChange={(c) => setTxnForm({ ...txnForm, is_recurring: c })}
                data-testid="txn-recurring"
              />
            </div>
            {txnForm.is_recurring && (
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select
                  value={txnForm.recurring_frequency}
                  onValueChange={(v) => setTxnForm({ ...txnForm, recurring_frequency: v })}
                >
                  <SelectTrigger data-testid="txn-frequency">
                    <SelectValue placeholder="Select frequency" />
                  </SelectTrigger>
                  <SelectContent>
                    {RECURRING_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Optional notes..."
                value={txnForm.notes}
                onChange={(e) => setTxnForm({ ...txnForm, notes: e.target.value })}
                data-testid="txn-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={saveTransaction} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="save-transaction-btn">
              {editingTxn ? "Update" : "Add Transaction"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Categorize Dialog */}
      <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Bulk Categorize</DialogTitle>
            <DialogDescription>Assign {selectedTxns.size} transactions to a category</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <Select value={bulkCategory} onValueChange={setBulkCategory}>
              <SelectTrigger data-testid="bulk-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORIES).filter(([k]) => k !== "uncategorized").map(([key, cat]) => (
                  <SelectItem key={key} value={key}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkDialog(false)}>Cancel</Button>
            <Button onClick={handleBulkCategorize} className="bg-[#1e3a5f] hover:bg-[#2d5a8e]" data-testid="confirm-bulk-btn">
              Apply to {selectedTxns.size} transactions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Transaction List Component
const TransactionList = ({
  transactions, selectedTxns, toggleSelectTxn, getAccountName,
  onEdit, onDelete, onCategorize, onReceiptUpload, uploadingReceipt, token
}) => {
  if (transactions.length === 0) {
    return (
      <Card>
        <CardContent className="pt-8 pb-8 text-center">
          <p className="text-muted-foreground">No transactions found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="space-y-2">
          {transactions.map((txn) => (
            <TransactionRow
              key={txn.id}
              txn={txn}
              isSelected={selectedTxns.has(txn.id)}
              onToggleSelect={() => toggleSelectTxn(txn.id)}
              accountName={getAccountName(txn.bank_account_id)}
              onEdit={() => onEdit(txn)}
              onDelete={() => onDelete(txn.id)}
              onCategorize={(cat) => onCategorize(txn.id, cat)}
              onReceiptUpload={(file) => onReceiptUpload(txn.id, file)}
              isUploading={uploadingReceipt === txn.id}
              token={token}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// Single Transaction Row
const TransactionRow = ({
  txn, isSelected, onToggleSelect, accountName,
  onEdit, onDelete, onCategorize, onReceiptUpload, isUploading, token
}) => {
  const cat = CATEGORIES[txn.category] || CATEGORIES.uncategorized;
  const isExpense = txn.type === "expense";
  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  return (
    <div
      className={`p-3 rounded-lg border transition-all ${
        isSelected ? "border-[#1e3a5f] bg-[#1e3a5f]/5/50" : txn.category === "uncategorized" ? "border-amber-200 bg-amber-50/30" : "border-stone-200 hover:border-stone-300"
      }`}
      data-testid={`transaction-row-${txn.id}`}
    >
      <div className="flex items-center gap-3">
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="w-4 h-4 rounded border-stone-300 accent-[#1e3a5f]"
          data-testid={`select-txn-${txn.id}`}
        />

        {/* Main info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="font-medium text-stone-900 text-sm truncate">{txn.description}</span>
            {txn.is_recurring && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-blue-300 text-blue-600">
                <svg className="w-3 h-3 mr-0.5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {txn.recurring_frequency || "Recurring"}
              </Badge>
            )}
            {txn.receipt_path && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-[#1e3a5f]/30 text-[#1e3a5f]">
                <svg className="w-3 h-3 mr-0.5 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                Receipt
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <span>{txn.date}</span>
            <span>·</span>
            <span>{accountName}</span>
            {txn.vendor_name && (
              <>
                <span>·</span>
                <span>{txn.vendor_name}</span>
              </>
            )}
          </div>
        </div>

        {/* Category badge - clickable to change */}
        <Select value={txn.category} onValueChange={onCategorize}>
          <SelectTrigger className={`w-auto h-7 text-xs border ${cat.bg} px-2`} data-testid={`categorize-${txn.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(CATEGORIES).map(([key, c]) => (
              <SelectItem key={key} value={key}>
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }}></span>
                  {c.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Amount */}
        <span className={`font-semibold text-sm w-24 text-right ${isExpense ? "text-red-600" : "text-[#1e3a5f]"}`}>
          {isExpense ? "-" : "+"}{formatCurrency(txn.amount)}
        </span>

        {/* Actions */}
        <div className="flex items-center gap-1">
          {/* Receipt upload */}
          <label className="cursor-pointer p-1.5 rounded hover:bg-stone-100 transition-colors" title="Upload receipt">
            <input
              type="file"
              accept="image/*,.pdf"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && onReceiptUpload(e.target.files[0])}
              disabled={isUploading}
            />
            {isUploading ? (
              <svg className="w-4 h-4 text-stone-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            ) : (
              <svg className="w-4 h-4 text-stone-400 hover:text-stone-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
            )}
          </label>
          <button onClick={onEdit} className="p-1.5 rounded hover:bg-stone-100 transition-colors" title="Edit" data-testid={`edit-txn-${txn.id}`}>
            <svg className="w-4 h-4 text-stone-400 hover:text-stone-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
            </svg>
          </button>
          <button onClick={onDelete} className="p-1.5 rounded hover:bg-red-50 transition-colors" title="Delete" data-testid={`delete-txn-${txn.id}`}>
            <svg className="w-4 h-4 text-stone-400 hover:text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
            </svg>
          </button>
        </div>
      </div>

      {/* Notes row */}
      {txn.notes && (
        <p className="text-xs text-stone-500 mt-1.5 ml-7 italic">{txn.notes}</p>
      )}
    </div>
  );
};

export default Transactions;
